import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout,
  startCaseStep, subjectRow, TROUBLE_CATEGORY_MISSING, TROUBLE_WRONG_HOUSEHOLD,
} from '../GuideKit';

/* Trading → Allocate Account → Allocate New Account / Sleeves or
 * Re-Allocate / Modify Existing Accounts.
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Trading
 * (version 1, activated 2026-10-04): screens "Allocate Account" (questions and
 * the details box), "Funds Not Available (Allocate Accounts)" when funds are
 * not ready, and "Allocate New Account / Sleeves" or "Re-Allocate / Modify
 * Existing Accounts", each holding the c:prosperityPortfolioMgr sleeve setup
 * panel. Both sub-categories share the question screen; only which setup
 * screen follows differs. The panel's own field labels are not in the flow
 * metadata, so this guide describes it without quoting them. Shared chrome
 * and policy copy live in GuideKit.js.
 */

const STEPS = [
  startCaseStep({
    recordType: 'Trading',
    category: 'Allocate Account',
    subCategory: 'Allocate New Account / Sleeves or Re-Allocate / Modify Existing Accounts',
    subject: subjectRow('Sleeve setup - 0989-022356'),
    lookups: [['Household', 'The client household. It decides which accounts the sleeve setup panel works with, and the advisor and office are taken from it.']],
    note: (
      <>
        <strong>Both sub-categories ask the same questions.</strong> The Sub-Category decides which setup
        screen follows - one for a new sleeve setup, one for modifying an existing one.
      </>
    ),
  }),
  {
    title: 'Answer the Allocate Account questions',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          Questions reveal themselves one at a time based on your answers, so you only see what applies. Every
          question in the reference below is required once it appears.
        </p>
        <p style={{ margin: 0 }}>
          The last one is a box: <em>Please provide all relevant information for the Trading Team...</em> It
          becomes the case description Trading reads first, so describe the allocation you want - target models,
          sleeves and amounts or percentages. Click Next.
        </p>
      </>
    ),
  },
  {
    title: 'Set up or modify the sleeves',
    body: (
      <p style={{ margin: 0 }}>
        The next screen is titled <strong>Allocate New Account / Sleeves</strong> or{' '}
        <strong>Re-Allocate / Modify Existing Accounts</strong> to match your Sub-Category (the modify panel is
        headed <em>Modify Existing Sleeve(s)</em>). It holds Prosperity&rsquo;s sleeve setup panel, which is
        required. For a new setup, save your entries inside the panel before you click Next. What you enter is
        linked to the case for Trading.
      </p>
    ),
  },
  {
    title: 'Acknowledge and submit',
    body: (
      <p style={{ margin: 0 }}>
        Read the Operations and Trading notice under the panel - it governs timing and what the teams can and
        cannot do. Select <em>I have read, understand, and agree to the above information</em> under
        Acknowledgement and click Next. FSC creates the case and opens it. Note the case number for follow-up.
      </p>
    ),
  },
];

// Every question on the Allocate Account screen, in the order it can appear.
const QUESTIONS = [
  {
    screen: 'Allocate Account',
    q: 'Are you setting up new Sleeves or modifying an existing setup?',
    when: 'Always - this is the first question',
    options: ['This is a New Sleeve Setup/Allocation', 'I am Modifying an existing Sleeve Setup/Allocation'],
    note: 'Answer to match the Sub-Category you chose on the first screen.',
  },
  {
    q: 'Are Funds Available (Are these sleeves ready to Trade upon Sleeve Setup / Allocation Update)?',
    when: 'Once the first question is answered',
    options: ['Yes (Funds are available / Ready for Trading)', 'No (Funds are NOT available / NOT Ready for Trading)'],
    note: 'Answering No ends the questions on this screen, and Next takes you to a Funds Not Available message - see the warning below the list.',
    warn: true,
  },
  {
    q: "Is the Risk Tolerance changing for the Household and / or are these Trades within the associated Clients' Risk Tolerance Range?",
    when: 'Only if funds are available',
    options: [
      'Yes (The Risk Tolerance is Changing / Are outside the Clients Risk Tolerance Range)',
      'No (The Risk Tolerance is NOT Changing / Is Within the Clients Risk Tolerance Range)',
    ],
    note: 'Answering No skips the IPS question below.',
  },
  {
    q: 'Do you have an updated IPS or IPS Addendum?',
    when: 'Only if the risk tolerance is changing or the trades are outside the range',
    options: [
      'Yes (I have a signed IPS/Updated IPS or IPS Addendum)',
      'No (I do not have an IPS/Updated IPS or IPS Addendum)',
    ],
  },
  {
    q: 'Is this for a Qualified or Non-Qualified account?',
    when: 'Once the risk tolerance (and IPS, if asked) questions are answered',
    options: ['Qualified', 'Non-Qualified'],
    note: 'Qualified goes straight to the details box.',
  },
  {
    q: 'Do you wish to add a Capital Gains budget? (Recommended to avoid adverse tax consequences for the Client)',
    when: 'Only for a Non-Qualified account',
    options: ['Yes (I wish to add a Capital Gains Budget)', 'No (I do not wish to add a Capital Gains Budget)'],
  },
  {
    q: 'Max Long Term Gains ($) · Max Short Term Gains ($)',
    when: 'Only if you chose to add a Capital Gains Budget',
    note: 'Both are required dollar amounts. They are saved to the case as the capital gains budget.',
  },
  {
    q: 'Capital Gains Details',
    when: 'Once either maximum is above $0',
    note: 'Required. Saved to the case as the capital gains notes.',
  },
  {
    q: 'Please provide all relevant information for the Trading Team...',
    when: 'For a Qualified account, or once the capital gains questions are done',
    note: 'Required. Becomes the case description.',
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Trading', 'Allocate Account'),
  {
    h: 'I got a "Funds Not Available (Allocate Accounts)" message',
    p: [
      'You said the funds are not available or not ready for trading. Trading cannot allocate or trade an account until the funds are settled and available. The message has no Next button - close the form and open a new case once the funds are available.',
      'Questions: tradingops@prosperitycapitaladvisors.com, as the message says.',
    ],
  },
  {
    h: 'I got the wrong setup screen',
    p: [
      'The setup screen follows the Sub-Category on the first screen, not your answer to "Are you setting up new Sleeves or modifying an existing setup?". If they disagree, cancel and start again with the right Sub-Category.',
    ],
  },
  {
    h: "The details box hasn't appeared",
    p: ['It appears once you have answered the Qualified / Non-Qualified question and, for a Non-Qualified account, the capital gains questions.'],
  },
  TROUBLE_WRONG_HOUSEHOLD,
];

export default function AllocateAccount() {
  return (
    <GuidePage
      title="Allocate Account Requests"
      icon="pie-chart"
      path="Trading → Allocate Account → Allocate New Account / Sleeves or Re-Allocate / Modify Existing Accounts"
      minutes={5}
      intro="How to ask the Prosperity Trading team to set up new sleeves or change an existing sleeve allocation - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. The form branches on your answers, and some paths cannot be completed without them.</Para>
        <Bullets items={[
          <><strong>Settled, available funds</strong> in the account. If the funds are not ready to trade, the request cannot go ahead.</>,
          <><strong>The allocation you want</strong> - the sleeves and models, and the amounts or percentages for each.</>,
          <><strong>The risk tolerance answer</strong>, and if it is changing or the allocation falls outside it, whether you have a signed, updated IPS or IPS Addendum.</>,
          <><strong>Whether the account is qualified or non-qualified</strong>, and for a non-qualified account any capital gains budget - maximum long term and short term gains in dollars.</>,
        ]} />
        <Callout>
          If the funds are still in transit or unsettled, wait. Open the case once they are available.
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Use this to prepare before you sit down with a client, or to work out why a question you expected has not shown up.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext team="trading" />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It is not investment or tax advice, and it does not
        describe the suitability review of the allocation.
      </Footnote>
    </GuidePage>
  );
}
